/**
 * AuditLogsTable component - main entry point.
 *
 * This directory contains the split components:
 * - index.tsx: Main table component (this file)
 * - json-modal.tsx: Reusable JSON code block modal
 * - filter-utils.ts: Filter utilities and constants
 * - table-columns.tsx: Column definitions
 * - table-filters.tsx: Filter bar component
 * - row-actions.ts: Row actions configuration
 */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Box, Text, Banner, Stack, DataTable } from '@customerio/pluma-components/react';
import type {
  AuditLogResponse,
  AuditLogFilters,
  AuditLogEntry,
  AuditLogUser,
  AuditLogServiceAccount,
} from '../../types';
import { formatUserName } from '../../utils/user-formatting';
import { AuditLogExpandedDetails } from '../audit-log-expanded-details';
import type { UserInfo } from '../audit-log-expanded-details/value-renderers';
import { DiffModal } from '../diff-modal';
import { JsonCodeBlockModal } from './json-modal';
import { createAuditLogColumns } from './table-columns';
import { AuditLogsFilters } from './table-filters';
import { createRowActions } from './row-actions';
import {
  LOADING_SKELETON_ROW_COUNT,
  initializeFilterValues,
  getDefaultDateRange,
  type FilterValue,
} from './filter-utils';
import { getAuditLogDayLimit } from '../../utils/access';
import { isEmptyString } from '@/utils/string';
import { useAuth } from '@/services/auth';

interface RoleDefinition {
  id: number;
  name: string;
}

interface AuditLogsTableProps {
  data?: AuditLogResponse;
  isLoading?: boolean;
  error?: unknown;
  filters: AuditLogFilters;
  /**
   * Callback when filters change. Should be memoized with useCallback to prevent unnecessary re-renders.
   */
  onFiltersChange: (filters: Partial<AuditLogFilters>) => void;
  /**
   * Callback to set UI-only object type in URL (e.g., 'APITriggeredBroadcast') to preserve UI selection
   * when backend uses 'Campaign' for both Campaign and API-Triggered Broadcast
   */
  onSetUiObjectType?: (uiObjectType: string | null) => void;
  /**
   * UI-only object type from URL (e.g., 'APITriggeredBroadcast') to preserve UI selection
   * when backend uses 'Campaign' for both Campaign and API-Triggered Broadcast
   */
  uiObjectType?: string | null;
  context: 'account' | 'workspace';
  contextId: number;
  highlightedLogId?: string | null;
  allUsers: UserInfo[];
  isLoadingUsers?: boolean;
  onLoadUsers?: () => void;
  campaigns?: Array<{ id: number; name: string }>;
  isLoadingCampaigns?: boolean;

  segments?: Array<{ id: number; name: string }>;
  isLoadingSegments?: boolean;
  newsletters?: Array<{ id: number; name: string }>;
  isLoadingNewsletters?: boolean;
  transactionalMessages?: Array<{ id: number; name: string }>;
  isLoadingTransactionalMessages?: boolean;
  workspaces?: Array<{ id: number; name: string }>;
  roleDefinitions?: Record<number, RoleDefinition> | null;
  objectTypes?: Array<{ id: number; name: string }>;
  messages?: Array<{ output_id: string; subject?: string; name?: string; type?: string; campaign_id?: number }>;
  isLoadingMessages?: boolean;
}

// Stable empty array to avoid creating new array reference on every render
const EMPTY_LOGS: AuditLogEntry[] = [];

function AuditLogsEmptyState() {
  return (
    <Box p="800" textAlign="center">
      <Text>No audit logs found matching your criteria. Check your date range and filters.</Text>
    </Box>
  );
}

export const AuditLogsTable: React.FC<AuditLogsTableProps> = ({
  data,
  isLoading,
  error,
  filters,
  onFiltersChange,
  onSetUiObjectType,
  uiObjectType,
  context,
  contextId,
  highlightedLogId,
  allUsers,
  isLoadingUsers = false,
  onLoadUsers,
  campaigns = [],
  isLoadingCampaigns = false,

  segments = [],
  isLoadingSegments = false,
  newsletters = [],
  isLoadingNewsletters = false,
  transactionalMessages = [],
  isLoadingTransactionalMessages = false,
  workspaces = [],
  roleDefinitions,
  objectTypes = [],
  messages = [],
  isLoadingMessages: _isLoadingMessages = false,
}) => {
  const auth = useAuth();
  const dayLimit = getAuditLogDayLimit(auth.account);

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Filter state for DataTable integrated filters
  const [filterValues, setFilterValues] = useState<FilterValue[]>(() =>
    initializeFilterValues(filters, dayLimit, uiObjectType),
  );

  // Sync filter values when filters, day limit, or uiObjectType changes
  useEffect(() => {
    setFilterValues(initializeFilterValues(filters, dayLimit, uiObjectType));
  }, [filters, dayLimit, uiObjectType]);

  // Sync default date range to parent on mount if no date filters are set
  const hasInitializedDateRange = useRef(false);
  useEffect(() => {
    if (hasInitializedDateRange.current === true) return;
    hasInitializedDateRange.current = true;

    if (
      (filters.start_time == null || filters.start_time === '') &&
      (filters.end_time == null || filters.end_time === '')
    ) {
      const defaultRange = getDefaultDateRange();
      onFiltersChange({
        start_time: `${defaultRange.start.toString()}T00:00:00Z`,
        end_time: `${defaultRange.end.toString()}T23:59:59Z`,
      });
    }
  }, [filters.start_time, filters.end_time, onFiltersChange]);

  // Logs are now filtered server-side via campaign_type parameter
  // No client-side filtering needed - backend handles Campaign vs APITriggeredBroadcast distinction
  const logs = useMemo(() => data?.data ?? EMPTY_LOGS, [data?.data]);
  const users = useMemo(() => data?.users ?? [], [data?.users]);
  const nextCursor = data?.pagination?.next_cursor ?? undefined;
  const hasMore = nextCursor !== undefined && nextCursor !== null && nextCursor !== 0;

  const handleLoadMore = useCallback(async () => {
    // hasMore is derived from nextCursor, so nextCursor check is redundant
    if (hasMore !== true || isLoadingMore === true || nextCursor == null) return;

    setIsLoadingMore(true);
    try {
      onFiltersChange({ cursor: nextCursor.toString() });
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, nextCursor, onFiltersChange]);

  // Create a lookup map for users by ID for O(1) lookups instead of O(n) array searches
  // Use AuditLogUser type to accommodate both allUsers (required fields) and users (optional fields)
  const usersById = useMemo(() => {
    const map = new Map<number, AuditLogUser>();
    // Add allUsers first (from API)
    for (const user of allUsers) {
      map.set(user.user_id, user);
    }
    // Add users from data (fallback)
    for (const user of users) {
      if (map.has(user.user_id) === false) {
        map.set(user.user_id, user);
      }
    }
    return map;
  }, [allUsers, users]);

  // Create a lookup map for service accounts by ID
  const serviceAccountsById = useMemo(() => {
    const map = new Map<number, AuditLogServiceAccount>();
    for (const sa of data?.service_accounts ?? []) {
      map.set(sa.service_account_id, sa);
    }
    return map;
  }, [data?.service_accounts]);

  // Memoized function to get user display name
  const getUserDisplayName = useCallback(
    (userId: number) => {
      if (userId === 0) {
        return 'System';
      }
      const user = usersById.get(userId);
      if (user == null) {
        return 'Unknown';
      }
      return formatUserName(user);
    },
    [usersById],
  );

  // Convert roleDefinitions to array format for filter dropdown
  const roles = useMemo(() => {
    if (roleDefinitions == null) return [];
    return Object.values(roleDefinitions).map((role) => ({
      id: role.id,
      name: role.name,
    }));
  }, [roleDefinitions]);

  // Derive sorting from filters.sort_order
  // Temporarily hidden from UI until backend fixes sort issues
  const sorting = useMemo(
    () => ({
      id: 'timestamp',
      desc: filters.sort_order === 'desc',
    }),
    [filters.sort_order],
  );

  // Memoize DataTable configuration objects
  // Sorting UI is disabled but backend still uses sort_order filter
  // Prefixed with _ to indicate intentionally unused (will be used when sorting is re-enabled)
  const _sortingConfig = useMemo(
    () => ({
      value: sorting,
      onChange: () => {
        // Temporarily disabled - no-op until backend fixes sort issues
      },
      isManual: true,
    }),
    [sorting],
  );

  const loadMoreConfig = useMemo(
    () => ({
      canLoad: hasMore,
      onClick: handleLoadMore,
      isLoading: isLoadingMore,
      label: 'Load More',
    }),
    [hasMore, handleLoadMore, isLoadingMore],
  );

  const columns = useMemo(
    () =>
      createAuditLogColumns({
        context,
        getUserDisplayName,
        usersById,
        serviceAccountsById,
        roleDefinitions,
        segments,
        workspaces,
        newsletters,
        transactionalMessages,
        campaigns,
        contextId,
      }),
    [
      context,
      getUserDisplayName,
      usersById,
      serviceAccountsById,
      roleDefinitions,
      segments,
      workspaces,
      newsletters,
      transactionalMessages,
      campaigns,
      contextId,
    ],
  );

  // Error banner state
  const [isDismissed, setIsDismissed] = useState(false);

  // Modal states
  const [isRawModalOpen, setIsRawModalOpen] = useState(false);
  const [selectedLogForRaw, setSelectedLogForRaw] = useState<AuditLogEntry | null>(null);
  const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false);
  const [selectedLogForMetadata, setSelectedLogForMetadata] = useState<AuditLogEntry | null>(null);
  const [isDiffModalOpen, setIsDiffModalOpen] = useState(false);
  const [selectedLogForDiff, setSelectedLogForDiff] = useState<AuditLogEntry | null>(null);

  // Reset dismissed state when error changes
  useEffect(() => {
    if (error != null) {
      setIsDismissed(false);
    }
  }, [error]);

  // Clear filter handler for error banner
  const handleFilterClearAll = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    setFilterValues([
      { key: 'date_range', value: defaultRange },
      { key: 'user_id', value: 'all' },
      { key: 'object_type', value: 'all' },
    ]);
    onFiltersChange({
      user_id: undefined,
      ip_addr: undefined,
      object_type: undefined,
      object_id: undefined,
      attribute_name: undefined,
      action: undefined,
      start_time: `${defaultRange.start.toString()}T00:00:00Z`,
      end_time: `${defaultRange.end.toString()}T23:59:59Z`,
      cursor: undefined,
    });
  }, [onFiltersChange]);

  const errorTitle = 'Error loading audit logs';
  const errorMessage = 'Try adjusting your filters or clearing them to see results.';

  const errorBanner =
    error != null && isDismissed === false ? (
      <Banner
        variant="error"
        title={errorTitle}
        shouldShowDismissButton
        onClose={() => setIsDismissed(true)}
        action={{
          label: 'Clear filters',
          onClick: () => {
            handleFilterClearAll();
            setIsDismissed(true);
          },
        }}
      >
        <Text mt="200">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
          {'. '}
          {errorMessage}
        </Text>
      </Banner>
    ) : null;

  // ExpandedRowComponent
  const ExpandedRowComponent = useCallback(
    ({ row }: { row: { original: AuditLogEntry } }) => {
      const log = row.original as AuditLogEntry;
      return (
        <AuditLogExpandedDetails
          log={log}
          context={context}
          contextId={contextId}
          startTime={filters.start_time}
          endTime={filters.end_time}
          allUsers={allUsers}
          roleDefinitions={roleDefinitions}
          segments={segments}
          workspaces={workspaces}
          campaigns={campaigns}
          objectTypes={objectTypes}
          newsletters={newsletters}
          messages={messages}
        />
      );
    },
    [
      context,
      contextId,
      filters.start_time,
      filters.end_time,
      allUsers,
      roleDefinitions,
      segments,
      workspaces,
      campaigns,
      objectTypes,
      newsletters,
      messages,
    ],
  );

  // Row actions configuration
  const rowActions = useCallback(
    (row: { original: AuditLogEntry }) =>
      createRowActions({
        filterValues,
        setFilterValues,
        onFiltersChange,
        context,
        contextId,
        account: auth.account,
        onShowRaw: (log) => {
          setSelectedLogForRaw(log);
          setIsRawModalOpen(true);
        },
        onShowMetadata: (log) => {
          setSelectedLogForMetadata(log);
          setIsMetadataModalOpen(true);
        },
        onShowDiff: (log) => {
          setSelectedLogForDiff(log);
          setIsDiffModalOpen(true);
        },
      })(row),
    [filterValues, onFiltersChange, context, contextId, auth.account],
  );

  return (
    <Stack gap="400" mb="800">
      {/* Error Banner */}
      {errorBanner}

      {/* Custom Filter Bar */}
      <AuditLogsFilters
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        onFiltersChange={onFiltersChange}
        onSetUiObjectType={onSetUiObjectType}
        uiObjectType={uiObjectType}
        context={context}
        allUsers={allUsers}
        isLoadingUsers={isLoadingUsers}
        onLoadUsers={onLoadUsers}
        campaigns={campaigns}
        isLoadingCampaigns={isLoadingCampaigns}
        segments={segments}
        isLoadingSegments={isLoadingSegments}
        newsletters={newsletters}
        isLoadingNewsletters={isLoadingNewsletters}
        transactionalMessages={transactionalMessages}
        isLoadingTransactionalMessages={isLoadingTransactionalMessages}
        workspaces={workspaces}
        roles={roles}
      />

      {/* DataTable */}
      <DataTable
        caption="Audit log entries"
        data={error != null ? [] : logs}
        columns={columns}
        getRowId={(row) => row.id.toString()}
        withRowExpanding={true}
        expandedRowComponent={ExpandedRowComponent}
        getRowCanExpand={() => true}
        initiallyExpandedRows={
          highlightedLogId != null && isEmptyString(highlightedLogId) === false ? { [highlightedLogId]: true } : {}
        }
        isLoading={(isLoading ?? false) === true && error == null}
        loadingState={{ rowCount: LOADING_SKELETON_ROW_COUNT }}
        emptyStateComponent={AuditLogsEmptyState}
        layout="fixed"
        withSorting={false}
        loadMore={error != null ? undefined : loadMoreConfig}
        rowActions={rowActions}
      />

      {/* Raw Modal */}
      <JsonCodeBlockModal
        isOpen={isRawModalOpen}
        onClose={() => {
          setIsRawModalOpen(false);
          setSelectedLogForRaw(null);
        }}
        title="Raw Log Data"
        code={selectedLogForRaw != null ? JSON.stringify(selectedLogForRaw.data, null, 2) : ''}
        skeletonRows={12}
      />

      {/* Metadata Modal */}
      <JsonCodeBlockModal
        isOpen={isMetadataModalOpen}
        onClose={() => {
          setIsMetadataModalOpen(false);
          setSelectedLogForMetadata(null);
        }}
        title="Metadata"
        code={
          selectedLogForMetadata != null
            ? JSON.stringify(
                (() => {
                  const { meta, ip_addr } = selectedLogForMetadata;
                  const isPlainObject = typeof meta === 'object' && meta != null && Array.isArray(meta) === false;
                  if (isPlainObject) {
                    return { ...meta, ip_address: ip_addr ?? null };
                  }
                  return {
                    ...(meta !== undefined ? { meta } : {}),
                    ip_address: ip_addr ?? null,
                  };
                })(),
                null,
                2,
              )
            : ''
        }
        skeletonRows={8}
      />

      {/* Diff Modal - GitHub-style red/green diff view */}
      <DiffModal
        isOpen={isDiffModalOpen}
        onClose={() => {
          setIsDiffModalOpen(false);
          setSelectedLogForDiff(null);
        }}
        log={selectedLogForDiff}
      />
    </Stack>
  );
};

// Re-export utilities for external use
export * from './filter-utils';
